/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState } from "react";

interface RouterContextValue {
  pathname: string;
  navigate: (path: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);

export function RouterProvider({ children }: { children: React.ReactNode }) {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const value = useMemo<RouterContextValue>(
    () => ({
      pathname,
      navigate: (path, options) => {
        if (path === window.location.pathname) return;
        if (options?.replace) window.history.replaceState(null, "", path);
        else window.history.pushState(null, "", path);
        setPathname(path);
        window.scrollTo({ top: 0, behavior: "auto" });
      },
    }),
    [pathname],
  );

  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  );
}

export function useAppRouter(): RouterContextValue {
  const context = useContext(RouterContext);
  if (!context)
    throw new Error("useAppRouter must be used within RouterProvider.");
  return context;
}

export function useAppNavigate(): RouterContextValue["navigate"] {
  return useAppRouter().navigate;
}
